<template>
  <div class="wizard-step-inicio">
    <div class="step-header">
      <h3>
        <i class="pi pi-angle-right"></i>
        {{ t('Início - Chegada no serviço') }}
      </h3>
      <p class="step-description">
        {{
          t(
            'Clique nos botões para indicar se você já chegou no serviço e está tudo certo para começar, ou se houve algum tipo de problema com você, com o carro ou com o cliente e o serviço terá que ser cancelado ou reagendado.'
          )
        }}
      </p>
    </div>

    <!-- Check-in info (if already started) -->
    <div v-if="checkinStarted" class="checkin-info">
      <h4>
        <i class="pi pi-check-circle text-success"></i>
        {{ t('Check-in iniciado') }}
        <small v-if="checkinTime">{{ formatDateTime(checkinTime) }}</small>
      </h4>
    </div>

    <!-- Action buttons -->
    <div class="action-container">
      <div class="action-wrapper">
        <a href="javascript:void(0)" data-testid="checkin-confirm-btn" @click="handleNoProblems" class="action-card">
          <div class="info-box bg-green">
            <span class="info-box-icon"><i class="pi pi-check"></i></span>
            <div class="info-box-content">
              <span class="info-box-number">{{ t('Tudo certo!') }}</span>
              <span class="info-box-text">{{ t('Iniciar serviço') }}</span>
            </div>
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWizardServicos } from '../../../composables/useWizardServicos';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';

interface CompromissoShape {
  compromisso_id?: number | string;
  compromisso_checkin?: string | number | null;
  [key: string]: unknown;
}

interface Props {
  compromisso?: CompromissoShape | null;
}

const props = withDefaults(defineProps<Props>(), {
  compromisso: null
});

const emit = defineEmits<{
  'next-step': [];
}>();

const { t } = useI18n();
const { markNoProblems } = useWizardServicos();

// Computed
const checkinStarted = computed(() => {
  const checkin = props.compromisso?.compromisso_checkin;
  // Check if checkin is not a default/empty timestamp
  return (
    checkin &&
    checkin !== '978307200000' &&
    checkin !== '2001-01-01 00:00:00' &&
    checkin !== 0 &&
    checkin !== '0'
  );
});

const checkinTime = computed((): string | number | null | undefined => {
  if (checkinStarted.value) {
    return props.compromisso?.compromisso_checkin;
  }
  return null;
});

// Methods
const formatDateTime = (dateTime: string | number | null | undefined): string => {
  if (!dateTime) return '';

  try {
    // Try to parse the datetime string
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) {
      return String(dateTime); // Return as-is if can't parse
    }

    // Format: DD/MM/YYYY HH:mm:ss
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    log('WizardStep0Inicio', 'formatDateTime', 'Error formatting date', error);
    return String(dateTime ?? '');
  }
};

const handleNoProblems = async (): Promise<void> => {
  try {
    log('WizardStep0Inicio', 'handleNoProblems', 'Marking no problems', props.compromisso?.compromisso_id);

    await markNoProblems(Number(props.compromisso?.compromisso_id));

    eventBus.emit('globalMessage.success', t('Check-in realizado com sucesso'));

    // Emit event to move to next step
    emit('next-step');

    // Refresh wizard data
    eventBus.emit('wizard-servicos.refresh');

    log('WizardStep0Inicio', 'handleNoProblems', 'Successfully marked no problems');
  } catch (error) {
    log('WizardStep0Inicio', 'handleNoProblems', 'Error marking no problems', error);
    eventBus.emit('globalMessage.error', t('Erro ao realizar check-in'));
  }
};
</script>

<style scoped>
.wizard-step-inicio {
  padding: 20px;
}

.step-header {
  margin-bottom: 40px;
}

.step-header h3 {
  font-size: 24px;
  margin-bottom: 15px;
}

.step-description {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
}

.checkin-info {
  margin-bottom: 30px;
  padding: 15px;
  background-color: #f0f9ff;
  border-left: 4px solid #00a65a;
  border-radius: 4px;
}

.checkin-info h4 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.checkin-info small {
  font-size: 14px;
  color: #666;
  margin-left: 10px;
}

.action-card {
  display: block;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
}

.action-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  text-decoration: none;
}

.info-box {
  display: flex;
  align-items: center;
  min-height: 90px;
  border-radius: 4px;
  padding: 20px;
  cursor: pointer;
}

.bg-green {
  background-color: #00a65a;
  color: white;
}

.info-box-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 90px;
  font-size: 45px;
  flex-shrink: 0;
}

.info-box-content {
  flex: 1;
  padding-left: 20px;
}

.info-box-number {
  display: block;
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 5px;
}

.info-box-text {
  display: block;
  font-size: 16px;
  opacity: 0.9;
}

.text-success {
  color: #00a65a;
}

/* Action container - replaces Bootstrap grid */
.action-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

.action-wrapper {
  width: 50%;
  max-width: 600px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .action-wrapper {
    width: 100%;
  }

  .action-container {
    padding: 0 15px;
  }
  .step-header {
    margin-top: 40px;
  }

  .step-header h3 {
    font-size: 20px;
  }

  .step-description {
    font-size: 14px;
  }

  .info-box {
    flex-direction: column;
    text-align: center;
  }

  .info-box-icon {
    width: 70px;
    height: 70px;
    font-size: 35px;
    margin-bottom: 10px;
  }

  .info-box-content {
    padding-left: 0;
  }
}
</style>
